/**
 * Abbinatore v2 — API client tipizzato.
 *
 * Usa la istanza ``api`` condivisa con interceptor auth (vedi
 * ``services/api.ts``). Per ridurre duplicazione, alcune chiamate si
 * rivolgono al router v1 (``/api/abbinatore/*``).
 */
import api from '../../../services/api';

// ── Types ─────────────────────────────────────────────────────────

export interface DashStat {
  label: string;
  value: string;
  sub: string | null;
}
export interface TodoItem {
  code: string;
  desc: string;
  cat: string | null;
}
export interface ActivityItem {
  type: string;
  text: string;
  meta: string;
  ts: string;
}

export interface MachineRow {
  id: string;
  model: string;
  brand: string;
  brandCode: string | null;
  type: string | null;
  series: string | null;
  compat: number;
  lastAssoc: string | null;
  hasParent: boolean;
  rebrandFrom: string | null;
  rebrandModel: string | null;
  /** Relazione con il padre — presente solo sulle righe figlie. */
  kind?: 'similar' | 'rebrand';
  /** Figlie già risolte (solo con ``withChildren``). */
  children?: MachineRow[];
}

export interface ArticleRow {
  sku: string;
  desc: string;
  cat: string | null;
  brand: string | null;
  compat: number;
  lastAssoc: string | null;
}

export interface ArticleMachine {
  assocId: string;
  machineId: string;
  machine: string;
  brand: string;
  model: string;
  code: string;
}

export interface MachineArticle {
  assocId: string;
  sku: string;
  desc: string;
  brand: string;
  cat: string;
}

export interface PaginatedResp<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface MarcaOption { code: string; label: string }
export interface CategoriaOption { code: string; label: string }

// ── Dashboard ─────────────────────────────────────────────────────

export const fetchDashStats = () =>
  api.get<{ stats: DashStat[] }>('/api/abbinatore-v2/dashboard/stats').then(r => r.data.stats);

export const fetchDashActivity = (limit = 10) =>
  api.get<{ items: ActivityItem[] }>(`/api/abbinatore-v2/dashboard/activity?limit=${limit}`).then(r => r.data.items);

// ── Macchine ──────────────────────────────────────────────────────

export interface MachineFilters {
  search?: string;
  brand?: string;
  tipo?: string;
  unmatched?: boolean;
  hasSimilar?: boolean;
  excludeParents?: boolean;
  /** Allega a ogni macchina le figlie similari e rebrand, già espanse. */
  withChildren?: boolean;
  page?: number;
  pageSize?: number;
}

export const fetchMachines = (f: MachineFilters = {}) => {
  const params = new URLSearchParams();
  if (f.search) params.set('search', f.search);
  if (f.brand) params.set('brand', f.brand);
  if (f.tipo) params.set('tipo', f.tipo);
  if (f.unmatched) params.set('unmatched', 'true');
  if (f.hasSimilar) params.set('has_similar', 'true');
  if (f.excludeParents) params.set('exclude_parents', 'true');
  if (f.withChildren) params.set('with_children', 'true');
  params.set('page', String(f.page ?? 0));
  params.set('page_size', String(f.pageSize ?? 20));
  return api.get<PaginatedResp<MachineRow>>(`/api/abbinatore-v2/machines?${params}`).then(r => r.data);
};

// ── Compatibilità (macchine + rebrand affiancati) ────────────────

export interface RebrandChild {
  id: string;
  model: string;
  brand: string;
  type: string | null;
  compat: number;
  lastAssoc: string | null;
  catEsclCount: number;
  categorieEscluse: string[];
  reviewedAt: string | null;
}

export interface CompatRow {
  id: string;
  model: string;
  brand: string;
  type: string | null;
  compat: number;
  lastAssoc: string | null;
  rebrands: Record<string, RebrandChild[]>;
}

export const fetchRebrandBrands = (brand: string) =>
  api.get<{ brands: string[] }>(`/api/abbinatore-v2/rebrand-brands?brand=${encodeURIComponent(brand)}`).then(r => r.data.brands);

export const fetchRebrandBrandsPref = (brand: string) =>
  api.get<{ selected: string[] | null }>(`/api/abbinatore-v2/rebrand-brands-pref?brand=${encodeURIComponent(brand)}`).then(r => r.data.selected);

export const saveRebrandBrandsPref = (brand: string, selected: string[]) =>
  api.put(`/api/abbinatore-v2/rebrand-brands-pref`, { brand, selected }).then(r => r.data);

export interface CompatFilters {
  brand: string;
  tipo?: string;
  search?: string;
  filtro?: 'con_similari' | 'da_aggiornare';
  page?: number;
  pageSize?: number;
}

export const fetchCompatibilita = (f: CompatFilters) => {
  const params = new URLSearchParams();
  params.set('brand', f.brand);
  if (f.tipo) params.set('tipo', f.tipo);
  if (f.search) params.set('search', f.search);
  if (f.filtro) params.set('filtro', f.filtro);
  params.set('page', String(f.page ?? 0));
  params.set('page_size', String(f.pageSize ?? 50));
  return api.get<PaginatedResp<CompatRow>>(`/api/abbinatore-v2/compatibilita?${params}`).then(r => r.data);
};

// ── Articoli ──────────────────────────────────────────────────────

export interface ArticleFilters {
  search?: string;
  category?: string;
  brand?: string;
  unmatched?: boolean;
  page?: number;
  pageSize?: number;
}

export const fetchArticles = (f: ArticleFilters = {}) => {
  const params = new URLSearchParams();
  if (f.search) params.set('search', f.search);
  if (f.category) params.set('category', f.category);
  if (f.brand) params.set('brand', f.brand);
  if (f.unmatched) params.set('unmatched', 'true');
  params.set('page', String(f.page ?? 0));
  params.set('page_size', String(f.pageSize ?? 20));
  return api.get<PaginatedResp<ArticleRow>>(`/api/abbinatore-v2/articles?${params}`).then(r => r.data);
};

export const fetchArticleMachines = (sku: string) =>
  api.get<{ items: ArticleMachine[] }>(`/api/abbinatore-v2/articles/${encodeURIComponent(sku)}/machines`)
    .then(r => r.data.items);

export const fetchMachineArticles = (machineId: string) =>
  api.get<{ items: MachineArticle[] }>(`/api/abbinatore-v2/machines/${encodeURIComponent(machineId)}/articles`)
    .then(r => r.data.items);

export interface ArticleLite { sku: string; desc: string; brand: string; cat: string }

export const fetchArticlesBySkus = (skus: string[]) =>
  api.post<{ items: ArticleLite[] }>('/api/abbinatore-v2/articles/by-skus', { skus })
    .then(r => r.data.items);

// ── Abbinamento batch (3 modalità: dual / matrice / copia) ────────

export interface BatchAssocReq {
  skus: string[];
  machineIds: string[];
}
export interface BatchAssocResp {
  created: number;
  skipped_existing: number;
  /** Coppie non create perché l'articolo è in una categoria che la relazione
   *  padre↔figlia dichiara non condivisa (entrambe nella stessa selezione). */
  skipped_excluded: number;
  total_machines: number;
  total_skus: number;
}

export const createAssociations = (req: BatchAssocReq) =>
  api.post<BatchAssocResp>('/api/abbinatore-v2/associations', req).then(r => r.data);

export const deleteAssociations = (skus: string[], machineIds: string[]) =>
  api.delete<{ deleted: number }>('/api/abbinatore-v2/associations', { data: { skus, machineIds } }).then(r => r.data);

export type CellState = 'direct' | 'inherited' | 'none';

export interface MatrixResp {
  matrix: Record<string, Record<string, CellState>>;
  skus: string[];
  machineIds: string[];
}

export const fetchMatrix = (skus: string[], machineIds: string[]) =>
  api.post<MatrixResp>('/api/abbinatore-v2/matrix', { skus, machineIds }).then(r => r.data);

export const toggleMatrixCell = (sku: string, machineId: string, state: 'direct' | 'none') =>
  api.post<{ ok: boolean; action: string }>('/api/abbinatore-v2/matrix/cell', { sku, machineId, state }).then(r => r.data);

export interface CopyReq {
  source_type: 'article' | 'machine';
  source_id: string;
  target_ids: string[];
  mode?: 'merge' | 'replace';
  /** Escluse aggiuntive scelte a mano: si sommano a quelle della relazione. */
  categorieEscluse?: string[];
}
export const copyAssociations = (req: CopyReq) =>
  api.post<{ created: number; mode: string; skipped_excluded: number }>(
    '/api/abbinatore-v2/copy', req,
  ).then(r => r.data);

/** Categorie che la relazione (gruppo similari / rebrand) dichiara non
 *  condivise fra la macchina sorgente e ciascuna destinazione.
 *  Mappa `machineId → categorie`; le destinazioni senza relazione mancano. */
export const fetchRelationEscluse = (sourceId: string, targetIds: string[]) =>
  api.post<{ escluse: Record<string, string[]> }>(
    '/api/abbinatore-v2/relations/categorie-escluse', { sourceId, targetIds },
  ).then(r => r.data.escluse);

// ── Lookup ─────────────────────────────────────────────────────────
// Due usi, due endpoint: `fetchAbbinatoreFacets` dà le opzioni ristrette
// dalla marca (barre filtro), `fetchMarche/Categorie/Tipologie` le liste
// complete (form di creazione e import, dove serve poter scegliere anche
// un valore oggi inutilizzato).

export type FacetDomain = 'machines' | 'articles';

export interface AbbinatoreFacets {
  marche: MarcaOption[];
  tipologie: { code: string; label: string }[];
  categorie: CategoriaOption[];
}

export const fetchAbbinatoreFacets = (domain: FacetDomain, brand?: string | null) =>
  api.get<AbbinatoreFacets>('/api/abbinatore-v2/lookups/facets', {
    params: { domain, ...(brand ? { brand } : {}) },
  }).then(r => r.data);


export const fetchMarche = () =>
  api.get<{ marche: MarcaOption[] }>('/api/abbinatore-v2/lookups/marche').then(r => r.data.marche);

export const fetchCategorie = () =>
  api.get<{ categorie: CategoriaOption[] }>('/api/abbinatore-v2/lookups/categorie').then(r => r.data.categorie);

export const fetchTipologie = () =>
  api.get<{ tipologie: { code: string; label: string }[] }>('/api/abbinatore-v2/lookups/tipologie').then(r => r.data.tipologie);

// ── Similari (gruppi padre → figlie) ──────────────────────────────

export interface SimilarChild { id: string; nome: string; marca: string }
export interface SimilarGroup {
  parentId: string;
  parentNome: string;
  parentMarca: string;
  parentArticoliCount: number;
  categorieEscluse: string[];
  children: SimilarChild[];
  sharedCount: number;
}

export const fetchSimilarGroups = () =>
  api.get<{ groups: SimilarGroup[]; total: number }>('/api/abbinatore-v2/similar/groups').then(r => r.data);

export const createSimilarGroup = (parentId: string, childIds: string[], categorieEscluse: string[] = []) =>
  api.post<SimilarGroup>('/api/abbinatore-v2/similar/groups', { parentId, childIds, categorieEscluse }).then(r => r.data);

export const updateSimilarGroup = (parentId: string, childIds: string[], categorieEscluse: string[]) =>
  api.put<SimilarGroup>(`/api/abbinatore-v2/similar/groups/${encodeURIComponent(parentId)}`, { childIds, categorieEscluse }).then(r => r.data);

export const deleteSimilarGroup = (parentId: string) =>
  api.delete<{ detached: number }>(`/api/abbinatore-v2/similar/groups/${encodeURIComponent(parentId)}`).then(r => r.data);

// ── Rebrand (parent → children) ───────────────────────────────────

export interface RebrandMachine { id: string; brand: string; model: string; categorieEscluse: string[] }
export interface RebrandInfo { parent: RebrandMachine | null; children: RebrandMachine[] }

export const fetchMachineRebrands = (machineId: string) =>
  api.get<RebrandInfo>(`/api/abbinatore-v2/machines/${encodeURIComponent(machineId)}/rebrands`).then(r => r.data);

export const addRebrandChild = (parentId: string, childId: string, categorieEscluse: string[] = []) =>
  api.post<{ ok: boolean }>(`/api/abbinatore-v2/machines/${encodeURIComponent(parentId)}/rebrands/children`, { childId, categorieEscluse }).then(r => r.data);

export const removeRebrandChild = (parentId: string, childId: string) =>
  api.delete<{ ok: boolean }>(`/api/abbinatore-v2/machines/${encodeURIComponent(parentId)}/rebrands/children/${encodeURIComponent(childId)}`).then(r => r.data);

export const markRebrandsReviewed = (baseId: string) =>
  api.post<{ reviewed: number; checked: number }>(`/api/abbinatore-v2/machines/${encodeURIComponent(baseId)}/rebrands/mark-reviewed`).then(r => r.data);

export const updateMachine = (machineId: string, data: { brand?: string; tipo?: string; nome?: string }) =>
  api.put<{ ok: boolean; updated: number }>(`/api/abbinatore-v2/machines/${encodeURIComponent(machineId)}`, data).then(r => r.data);

export const createMachine = (data: { brand: string; nome: string; tipo?: string }) =>
  api.post<{ ok: boolean; id: string }>('/api/abbinatore-v2/machines', data).then(r => r.data);

// ── Import / Export catalogo (ZIP: macchine.csv + abbinamenti.csv) ──

export interface ImportExportStatus { machines: number; associations: number }

export const fetchImportExportStatus = () =>
  api.get<ImportExportStatus>('/api/abbinatore-v2/import-export/status').then(r => r.data);

export interface ExportFilters {
  scope: 'full' | 'machines' | 'articles';
  search?: string;
  brand?: string;
  tipo?: string;
  category?: string;
  unmatched?: boolean;
}

export const exportCatalog = (f: ExportFilters) => {
  const params = new URLSearchParams();
  params.set('scope', f.scope);
  if (f.search) params.set('search', f.search);
  if (f.brand) params.set('brand', f.brand);
  if (f.tipo) params.set('tipo', f.tipo);
  if (f.category) params.set('category', f.category);
  if (f.unmatched) params.set('unmatched', 'true');
  return api.get(`/api/abbinatore-v2/import-export/export?${params}`, { responseType: 'blob' })
    .then(r => ({ blob: r.data as Blob, filename: extractFilename(r.headers['content-disposition']) }));
};

export const downloadImportTemplate = () =>
  api.get('/api/abbinatore-v2/import-export/template', { responseType: 'blob' })
    .then(r => ({ blob: r.data as Blob, filename: extractFilename(r.headers['content-disposition']) }));

function extractFilename(disposition?: string): string {
  const m = disposition?.match(/filename=([^;]+)/);
  return m ? m[1].trim() : 'abbinatore_export.zip';
}

export interface ImportSummary {
  mode: 'replace' | 'merge';
  machines_created: number;
  machines_matched: number;
  rebrand_links_set: number;
  rebrand_links_skipped: number;
  associations_created: number;
  associations_skipped_existing: number;
  errors: string[];
  errors_total: number;
}

export const importCatalog = (file: File, mode: 'replace' | 'merge', confirm?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);
  if (confirm) formData.append('confirm', confirm);
  return api.post<ImportSummary>('/api/abbinatore-v2/import-export/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// ── Diff pre-import (dry-run) ────────────────────────────────────────

export interface ImportDiffItem {
  type: 'add' | 'update' | 'remove';
  entity: 'machine' | 'rebrand' | 'association';
  label: string;
  detail?: string | null;
}

export interface ImportDiff {
  mode: 'replace' | 'merge';
  machines_to_create: number;
  machines_matched: number;
  machines_to_remove: number;
  rebrand_to_set: number;
  rebrand_skipped: number;
  associations_to_create: number;
  associations_skipped_existing: number;
  associations_to_remove: number;
  items: ImportDiffItem[];
  items_total: number;
  items_truncated: boolean;
  errors: string[];
  errors_total: number;
}

export const previewImportCatalog = (file: File, mode: 'replace' | 'merge') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);
  formData.append('dry_run', 'true');
  return api.post<ImportDiff>('/api/abbinatore-v2/import-export/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
